import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ColorPickerDialogComponent } from '../color-picker-dialog.component';
import ColorHelpers from 'app/shared/helpers/color-helpers';

export interface GradientColorStop {
  color: string;
  position: number;
}

export type GradientType = 'linear' | 'radial';

@Component({
  selector: 'app-gradient-color-tool',
  templateUrl: './gradient-color-tool.component.html',
  styleUrls: ['./gradient-color-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GradientColorToolComponent implements OnChanges {
  private readonly dialog = inject(MatDialog);

  @Input() accountColors: any[] = [];
  @Input() currentGradient = '';
  @Input() sessionKey = 0;

  @Output() gradientUpdated = new EventEmitter<string>();
  @Output() gradientApplied = new EventEmitter<string>();
  @Output() closeGradientTool = new EventEmitter<void>();

  readonly initialGradient = signal<string>('');
  readonly isGradientChanged = computed(() => this.gradientCss() !== this.initialGradient());

  readonly gradientType = signal<GradientType>('linear');
  readonly angle = signal(45);
  readonly colorStops = signal<GradientColorStop[]>([
    { color: '#516ce3ff', position: 0 },
    { color: '#4cb5abff', position: 100 },
  ]);

  readonly activeStopIndex = signal<number | null>(null);

  readonly gradientCss = computed(() => {
    const stops = this.colorStops();
    const type = this.gradientType();
    if (stops.length < 2) {
      return '';
    }
    const stopStrings = stops.map((s) => `${s.color} ${s.position}%`).join(', ');
    if (type === 'radial') {
      return `radial-gradient(circle, ${stopStrings})`;
    }
    const deg = this.angle();
    return `linear-gradient(${deg}deg, ${stopStrings})`;
  });

  readonly gradientPreviewStyle = computed(() => {
    const css = this.gradientCss();
    return css ? { background: css } : { background: '#ccc' };
  });

  readonly sortedStops = computed(() => {
    return [...this.colorStops()].sort((a, b) => a.position - b.position);
  });

  readonly canRemoveStop = computed(() => this.colorStops().length > 2);
  readonly canAddStop = computed(() => this.colorStops().length < 4);

  ngOnChanges(changes: SimpleChanges): void {
    const sessionKeyChange = changes['sessionKey'];
    const gradientChange = changes['currentGradient'];

    if (sessionKeyChange && gradientChange) {
      // Both changed simultaneously = genuine component switch (or initial render).
      // Initialize gradient tool from the incoming currentGradient.
      if (ColorHelpers.isColorGradient(this.currentGradient)) {
        this.parseGradient(this.currentGradient);
        this.initialGradient.set(this.gradientCss());
      } else {
        this.gradientType.set('linear');
        this.angle.set(45);
        this.colorStops.set([
          { color: '#516ce3ff', position: 0 },
          { color: '#4cb5abff', position: 100 },
        ]);
        // No gradient on this component — Apply should be active so user can apply the preview
        this.initialGradient.set('');
      }
    } else if (gradientChange) {
      // Only gradient changed (user picked a color in parent picker, no component switch).
      if (ColorHelpers.isColorGradient(gradientChange.currentValue)) {
        this.parseGradient(gradientChange.currentValue);
      } else {
        // Non-gradient color selected — keep current stops unchanged, enable Apply button
        this.initialGradient.set('');
      }
    }
  }

  addStop(): void {
    if (!this.canAddStop()) return;
    const current = this.colorStops();
    const newCount = current.length + 1;
    const newPosition = Math.round(100 / newCount);
    const updatedStops = current.map((stop, i) => ({
      ...stop,
      position: Math.round((((i * 100) / (newCount - 1)) * (newCount - 1)) / newCount),
    }));
    // Distribute all positions evenly
    const allStops = [...updatedStops, { color: '#ffffffff', position: newPosition }].map((stop, i, arr) => ({
      ...stop,
      position: Math.round((i * 100) / (arr.length - 1)),
    }));
    this.colorStops.set(allStops);
    this.emitGradient();
  }

  removeStop(index: number): void {
    if (!this.canRemoveStop()) return;
    this.colorStops.update((current) => current.filter((_, i) => i !== index));
    this.emitGradient();
  }

  updateStopColor(index: number, color: string): void {
    this.colorStops.update((current) => current.map((stop, i) => (i === index ? { ...stop, color } : stop)));
    this.emitGradient();
  }

  updateStopPosition(index: number, position: number): void {
    const clampedPosition = Math.max(0, Math.min(100, position));
    this.colorStops.update((current) =>
      current.map((stop, i) => (i === index ? { ...stop, position: clampedPosition } : stop)),
    );
    this.emitGradient();
  }

  updateAngle(angle: number): void {
    this.angle.set(((angle % 360) + 360) % 360);
    this.emitGradient();
  }

  setGradientType(type: GradientType): void {
    this.gradientType.set(type);
    this.emitGradient();
  }

  openColorPickerDialog(index: number): void {
    const stop = this.colorStops()[index];
    const dialogRef = this.dialog.open(ColorPickerDialogComponent, {
      data: { currentColor: stop.color, accountColors: this.accountColors },
      panelClass: 'gradient-color-picker-dialog',
      autoFocus: false,
    });

    dialogRef.componentInstance.colorUpdated.subscribe((color: string) => {
      this.updateStopColor(index, color);
    });
  }

  applyGradient(): void {
    const css = this.gradientCss();
    this.initialGradient.set(this.gradientCss());
    if (css) {
      this.gradientApplied.emit(css);
    }
  }

  private emitGradient(): void {
    const css = this.gradientCss();
    if (css) {
      this.gradientUpdated.emit(css);
    }
  }

  private parseGradient(value: string): void {
    const linearMatch = value.match(/linear-gradient\(\s*(\d+)deg\s*,\s*(.+)\)/);
    const radialMatch = value.match(/radial-gradient\(\s*circle\s*,\s*(.+)\)/);

    if (!linearMatch && !radialMatch) return;

    let stopsString: string;
    if (radialMatch) {
      this.gradientType.set('radial');
      stopsString = radialMatch[1];
    } else {
      this.gradientType.set('linear');
      const angle = parseInt(linearMatch![1], 10);
      this.angle.set(angle);
      stopsString = linearMatch![2];
    }
    const stopRegex = /(#[0-9a-fA-F]{6,8})\s+(\d+)%/g;
    const stops: GradientColorStop[] = [];
    let stopMatch: RegExpExecArray | null;

    while ((stopMatch = stopRegex.exec(stopsString)) !== null) {
      let color = stopMatch[1];
      if (color.length === 7) {
        color += 'ff';
      }
      stops.push({ color: color.toLowerCase(), position: parseInt(stopMatch[2], 10) });
    }

    // Handle first color without explicit position
    if (stops.length === 0) {
      const firstColorMatch = stopsString.match(/#[0-9a-fA-F]{6,8}/);
      if (firstColorMatch) {
        let color = firstColorMatch[0];
        if (color.length === 7) color += 'ff';
        stops.unshift({ color: color.toLowerCase(), position: 0 });
      }
    } else {
      const beforeFirst = stopsString.substring(0, stopsString.indexOf(stops[0].color));
      const firstNoPos = beforeFirst.match(/#[0-9a-fA-F]{6,8}/);
      if (firstNoPos && firstNoPos[0].toLowerCase() !== stops[0].color) {
        let color = firstNoPos[0];
        if (color.length === 7) color += 'ff';
        stops.unshift({ color: color.toLowerCase(), position: 0 });
      }
    }

    if (stops.length >= 2) {
      this.colorStops.set(stops);
    }
  }
}
